// ================= SEASON 1: SCENES FROM SUMMER =================
//
// The first seasonal content drop. deploy_handoff/scenes-from-summer-plan.md is the integration
// plan; the designer's package (`Sparring_Cards_Scenes_from_Summer_Seasonal_Cards.zip`, canonical
// data at `data/scenes_from_summer_cards.v1.json`) is the authority for what these cards DO.
//
// SEVENTEEN CARDS, IN TWO KINDS THAT ARE NOT INTERCHANGEABLE. Two are AI-only props at
// `ownershipTier: 'Challenge'` — nobody's to own, ever, at any price. The other fifteen are
// ordinary Style/Family/Universal cards that a player BUYS, at 50 credits each, and then builds
// with like any other card.
//
// The fifteen landed in Phase C rather than Phase A and the gap was not scheduling: `legalPool` is
// permissive within a tier, so registering them before entitlements existed would have handed every
// player the whole season free, and that cannot be taken back gracefully. They arrived in the same
// work as the gate that sells them (lib/store/catalog.js, functions/api/store/purchase.js,
// library.html). The price is NOT a field on any card here — it lives in the catalog, server-side,
// where one edit changes it for everybody.
//
// TRANSCRIBED, NOT INGESTED, and that is a deliberate deviation from the package's
// INTEGRATION_CONTRACT.md, which asks the engine to "dispatch on `effect.type` and
// `condition.kind`" from its JSON at runtime. This engine has never worked that way: a card is
// authored JS whose hooks close over a ctx of named helpers (`grantOpening`, `setOffBalance`,
// `shiftFlow`, …), and its taxonomy rides in a `v4` block. Building a second, data-driven effect
// runtime beside the one 194 cards already use would mean two ways for a card to do the same thing
// and two places for a rules bug to hide.
//
// So the designer's structured effects are the SPECIFICATION each hook below is written against,
// and the specification is quoted in a comment beside every one of them. What the cards do is the
// designer's; how they are expressed here is this codebase's.
//
// WHERE THE TRANSLATION MAKES A JUDGEMENT, it is called out at the hook. Two are worth knowing up
// front:
//   `anyStrikeDealsDamage` -> `result.damage > 0`. On the multi-strike path `damage` is the total
//   across strikes and no strike can deal negative damage, so "the total is above zero" and "some
//   strike dealt damage" are the same statement.
//   `cleanHit` -> `result.cleanHitEffectsAllowed`, which is `clean && effectiveness === 'Optimal'`.
//   That is what a clean hit MEANS in this ruleset for the purpose of triggering an effect, and it
//   is the gate every comparable shipped card uses. Reading it as bare `result.clean` would make
//   this card's Off-Balance fire from Compromised range, which no other card's does.
//
// WHAT THE DESIGNER DID NOT SUPPLY and is authored here: `attackBeat`/`defenseBeat` (the narrative
// layer needs a prose line per half), `defenseMethod`, the printed `text` in house grammar, and the
// `v4` badge/summary strings. All of it is written to the package's own tone — the summer comedy
// belongs to these two prop cards and the fifteen player cards stay serious.
//
// AI-ONLY IS EXPRESSED AS `ownershipTier: 'Challenge'`, the tier that shipped in a141cb6. It is
// already excluded from `DeckRules.legalPool` (exclusion is the default for an unrecognised tier),
// reported by `validate` as its own CHALLENGE_ONLY issue, and framed in `--challenge` gold by
// `CardTile.ownershipColor`. Nothing new is needed to keep these out of a player's hands; these are
// simply the first two cards that exercise it.
//
// LOADED AFTER js/cards-v4.js, which matters: that file attaches `v4` by looping over its own 194
// ids and would not touch these, but the `v4` blocks below are authored inline and must not be
// overwritten by a later pass. Nothing else in the boot order cares.
(function () {
  'use strict';

  // Stamped on every card this season adds, and READ BY THE TESTS rather than by the game. Several
  // suites pin the archive at exactly 194 cards — the Speed Edition is a fixed historical artefact
  // and those comparisons are meant to keep running against exactly it. A season that grew the pool
  // without a way to say which cards are the archive's would have forced every one of those pins to
  // a new magic number, which is how a pin stops meaning anything. `seasonal` is that way: filter it
  // out and the archive is still the archive.
  const SEASON = 'scenes-from-summer-2026';

  Object.assign(window.CARDS, {

    // ---------------------------------------------------------------------------------------------
    // SFS-AI-01 — Beach Ball
    //
    // Designer: "Spike the beach ball high, then ricochet it into the body." Two strikes from one
    // Attack card, High then Mid, Power 1 each; if either lands, the opponent is opened LOW — the
    // line the ball's bounce leaves unguarded, and the line the Beach Bum's Muay Thai answers with.
    // ---------------------------------------------------------------------------------------------
    sfs_ai_beach_ball: {
      id: 'sfs_ai_beach_ball', name: 'Beach Ball', style: 'Universal', tempo: 16,
      ownershipTier: 'Challenge', ownershipGroup: 'ChallengeOnly',
      seasonal: SEASON, displayId: 'SFS-AI-01',
      // The designer's tags, minus `ChallengeOnly` — that one is not a property of the technique,
      // it is the ownership tier, and carrying it as a trait too would print a badge that repeats
      // what the frame already says. `MultiStrike` is likewise dropped: `attack.strikes` IS the
      // multi-strike, and a trait asserting it could come to disagree with the array.
      traits: ['Prop', 'Thrown'],
      moveGloss: 'Spike the beach ball high, then ricochet it into the body.',
      attackBeat: 'spikes a huge striped beach ball down at their head, then bounces it into the ribs',
      defenseBeat: 'ducks in behind the beach ball and gives up a step',
      defenseMethod: 'Backstep',
      attack: {
        optimalRanges: ['Long', 'Pocket'], cost: 0,
        // target.mode 'sequence', sequence [High, Mid], power 1. Levels are printed on the strikes
        // rather than chosen, which is what `mode: 'sequence'` means and what the engine reads:
        // `strike.level || chosenLevels[i]`.
        strikes: [{ level: 'High', power: 1 }, { level: 'Mid', power: 1 }],
        // effects[0]: createOpening, timing aftermath, target opponent,
        //   opening { type General, level Low, bonus 1, usableBy anyAttack, available
        //   nextExchangeOnly }, condition { kind: anyStrikeDealsDamage }.
        //
        // `ctx.opp` on an ATTACK hook is the defender, which is the designer's `target: opponent`.
        // General is grantOpening's default type and is passed explicitly anyway: the default is a
        // property of the helper and the type is a property of the card, and if the helper's default
        // ever changed this card would change with it silently.
        onResolved: (ctx, result) => {
          if (result.damage > 0) ctx.grantOpening(ctx.opp, 'Low', 1, { type: 'General' });
        },
        text: 'Opt L/P • H then M • P1 each. Apply Defense separately to each strike. If either ' +
              'strike deals damage, create a Low Opening on the opponent.'
      },
      defense: {
        levels: ['High', 'Mid', 'Low'], guard: 3,
        text: 'H/M/L • G3.'
      },
      // Dodge's whiff conversion and its End shift are resolved by the engine from this block
      // (§13) — there is no card-side hook for either, which is why the defense above is data only.
      v4: {
        technique: 'Thrown Beach Ball', family: null, speedPips: 3, speedLabel: 'Fast',
        continuation: { kind: 'Authored', summary: 'Conditional Low Opening', trigger: 'cardText' },
        defenseFamily: 'Dodge',
        defenseResult: {
          contact: false, attackBecomesWhiff: true,
          end: { kind: 'shift', direction: 'farther', bands: 1 }
        },
        attackBadge: 'THROWN BEACH BALL - AUTHORED: Conditional Low Opening.',
        defenseBadge: 'DODGE - Attack Whiffs; move End Range 1 band farther.',
        atkSummary: 'Range L/P | Level H, M | 2 strikes | Thrown Beach Ball',
        defSummary: 'Level H/M/L | Guard 3'
      }
    },

    // ---------------------------------------------------------------------------------------------
    // SFS-AI-02 — Metal Detector
    //
    // Designer: "Sweep the detector low, then brace the shaft across the body." A Low attack at
    // Power 3 that takes the base out from under a clean hit, and a Deny that plants the exchange
    // in the Pocket where the Beach Bum's clinch tools live.
    // ---------------------------------------------------------------------------------------------
    sfs_ai_metal_detector: {
      id: 'sfs_ai_metal_detector', name: 'Metal Detector', style: 'Universal', tempo: 34,
      ownershipTier: 'Challenge', ownershipGroup: 'ChallengeOnly',
      seasonal: SEASON, displayId: 'SFS-AI-02',
      traits: ['Prop', 'ImprovisedWeapon'],
      moveGloss: 'Sweep the detector low, then brace the shaft across the body.',
      attackBeat: 'sweeps the metal detector along the sand and scythes it into their lead leg',
      defenseBeat: 'braces the detector shaft across the body and refuses to be moved off',
      defenseMethod: 'Frame',
      attack: {
        optimalRanges: ['Pocket', 'Clinch'], levels: ['Low'], power: 3, cost: 0,
        // effects[0]: setOffBalance, timing afterDamage, target opponent, duration nextExchange,
        //   condition { kind: cleanHit }.
        //
        // `cleanHitEffectsAllowed` rather than `clean`: see the header. Off-Balance already lasts
        // exactly to the end of the next exchange in this engine (`_offBalanceJustGranted` carries
        // it one exchange and the end-of-exchange sweep clears it), so `duration: nextExchange` is
        // the default behaviour of the helper and needs nothing added here.
        onResolved: (ctx, result) => {
          if (result.cleanHitEffectsAllowed) ctx.setOffBalance(ctx.opp);
        },
        text: 'Opt P/C • L • P3. On a clean hit, the opponent is Off-Balance.'
      },
      defense: {
        levels: ['High', 'Mid', 'Low'], guard: 3,
        text: 'H/M/L • G3.'
      },
      // control { kind: setRange, range: Pocket } is this engine's `control: 'setPocket'` — the same
      // string Clinch Dump, Thrust Kick and Foot Stomp already use, resolved by the Deny branch.
      v4: {
        technique: 'Improvised Detector Sweep', family: null, speedPips: 2, speedLabel: 'Standard',
        continuation: { kind: 'Authored', summary: 'Conditional Off-Balance', trigger: 'cardText' },
        defenseFamily: 'Deny',
        defenseResult: { contact: false, cancelContinuation: true, control: 'setPocket' },
        attackBadge: 'IMPROVISED DETECTOR SWEEP - AUTHORED: Conditional Off-Balance.',
        defenseBadge: 'DENY - Cancel continuation; set End at Pocket.',
        atkSummary: 'Range P/C | Level L | Power P3 | Improvised Detector Sweep',
        defSummary: 'Level H/M/L | Guard 3'
      }
    },

    // =============================================================================================
    // THE FIFTEEN PLAYER CARDS (Phase C)
    //
    // One per Style, one per Family, three Universal. Bought with credits, never granted: they are
    // in `lib/store/catalog.js` at 100 apiece, and `DeckRules.legalPool`/`validate` keep them out of
    // a deck until the player owns them. Nothing about the CARDS says any of that — a store card is
    // an ordinary card of its tier in every mechanical respect, which is the point.
    //
    // THE DESIGNER'S BOUNDARY, worth knowing while reading them: not one of these fifteen attacks
    // LOW. Eight of them DEFEND low, several at Guard 4. The season is deliberately a set of
    // answers to the low line rather than more pressure on it, and tests/season-store-cards-test.js
    // pins that as a regression because it is exactly the dimension a transcription typo would move
    // without anything else noticing.
    //
    // The comedy stays on the two props above. These are serious sidegrades and their beats are
    // written that way.
    // =============================================================================================

    // ---------------------------------------------------------------------------------------------
    // SFS-001 — Level-Change Cross (Western Boxing)
    //
    // Designer: Punch, Speed 2, Pocket, Power 3, Mid. No conditional clause at all — a plain body
    // cross whose whole value is the Chain it leaves and a Deflect that covers Mid AND Low at Guard
    // 4, which is the "low-defense-access" tag the designer put on it.
    // ---------------------------------------------------------------------------------------------
    sfs_level_change_cross: {
      id: 'sfs_level_change_cross', name: 'Level-Change Cross', style: 'WesternBoxing', tempo: 26,
      ownershipTier: 'Style', ownershipGroup: 'WesternBoxing', family: 'TorquePressure',
      seasonal: SEASON, displayId: 'SFS-001',
      traits: ['Punch', 'LevelChange'],
      moveGloss: 'Dip beneath the lead shoulder and drive a cross to the body.',
      attackBeat: 'dips under the lead shoulder and drives a cross into the ribs',
      defenseBeat: 'drops the elbow across the body and rides the shot off the ribs',
      defenseMethod: 'Shell',
      attack: {
        optimalRanges: ['Pocket'], levels: ['Mid'], power: 3, cost: 0, category: 'Punch',
        text: 'Opt P • M • P3. Contact chains to a High Opening.'
      },
      defense: { levels: ['Mid', 'Low'], guard: 4, text: 'M/L • G4.' },
      v4: {
        technique: 'Level-Change Cross', family: 'Punch', speedPips: 2, speedLabel: 'Standard',
        continuation: { kind: 'Chain', openingLevel: 'High', openingType: 'Combination', bonus: 1,
                        usableBy: ['Punch'], available: 'nextExchangeOnly', trigger: 'contact' },
        defenseFamily: 'Deflect',
        defenseResult: { contact: false, cancelContinuation: true, openingOn: 'attacker',
                         openingLevel: 'attemptedLevelUnlessRecoveryPrinted', bonus: 1,
                         againstGrappleControl: 'holdClash' },
        attackBadge: 'PUNCH - CHAIN -> HIGH: Contact creates the printed Punch-only Opening for next exchange.',
        defenseBadge: 'DEFLECT - Cancel continuation; create an Opening on the attacker at the resolved exposure level.',
        atkSummary: 'Range P | Level M | Power P3 | Punch',
        defSummary: 'Level M/L | Guard 4'
      }
    },

    // ---------------------------------------------------------------------------------------------
    // SFS-002 — Long-Guard Spear Elbow (Muay Thai)
    //
    // Designer: +1 Power when `pressureMovedClashCloser` — you chose Pressure AND the clash landed
    // closer than it started. Both halves, which is why it reads the Intent as well as the movement:
    // a clash that drifted closer for someone else's reasons is not this card's payoff.
    // ---------------------------------------------------------------------------------------------
    sfs_long_guard_spear_elbow: {
      id: 'sfs_long_guard_spear_elbow', name: 'Long-Guard Spear Elbow', style: 'MuayThai', tempo: 28,
      ownershipTier: 'Style', ownershipGroup: 'MuayThai', family: 'TorquePressure',
      seasonal: SEASON, displayId: 'SFS-002',
      traits: ['Elbow', 'Pressure'],
      moveGloss: 'Post the lead hand and spear the rear elbow through the guard.',
      attackBeat: 'posts the lead hand and spears the rear elbow straight through the guard',
      defenseBeat: 'stacks the long guard high and refuses the elbow a lane',
      defenseMethod: 'LongGuard',
      attack: {
        optimalRanges: ['Pocket', 'Clinch'], levels: ['High'], power: 3, cost: 0, category: 'Elbow',
        // Designer: modifyPower +1, beforeGuard, condition `pressureMovedClashCloser`.
        // The shipped idiom for exactly this pair — see marching_pressure and torque_release.
        powerMod: (ctx) => (ctx.attChoice.positionIntent === 'Pressure' && ctx.clashCloser) ? 1 : 0,
        text: 'Opt P/C • H • P3. If you chose Pressure and Clash is closer than Start, +1 Power.'
      },
      defense: { levels: ['High', 'Mid'], guard: 3, text: 'H/M • G3.' },
      v4: {
        technique: 'Long-Guard Spear Elbow', family: null, speedPips: 2, speedLabel: 'Standard',
        continuation: { kind: 'ImpactRecovery', impactOnAbsorb: 1, recoveryOpeningLevel: 'High',
                        recoveryOpeningType: 'Recovery', recoveryOpeningBonus: 1,
                        recoveryTrigger: ['Deflect', 'Dodge'] },
        defenseFamily: 'Deny',
        defenseResult: { contact: false, cancelContinuation: true, control: 'setPocket' },
        attackBadge: 'LONG-GUARD SPEAR ELBOW - IMPACT/RECOVERY: Absorb costs 1 Stamina; Deflect or Dodge creates a High Recovery Opening.',
        defenseBadge: 'DENY - Cancel continuation; set End at Pocket.',
        atkSummary: 'Range P/C | Level H | Power P3 | Long-Guard Spear Elbow',
        defSummary: 'Level H/M | Guard 3'
      }
    },

    // ---------------------------------------------------------------------------------------------
    // SFS-003 — Gedan-Barai Counter (Shotokan Karate)
    //
    // Designer: +1 Power if the PAIRED DEFENSE fully guards at Low while this Attack is still
    // unresolved. That is cross_counter's clause with a level on it, and it is transcribed from
    // cross_counter deliberately — `attackMustBeUnresolved` is `ctx.actingSecond`, because their
    // Attack has to have already resolved against your committed Defense for the answer to exist.
    // ---------------------------------------------------------------------------------------------
    sfs_gedan_barai_counter: {
      id: 'sfs_gedan_barai_counter', name: 'Gedan-Barai Counter', style: 'ShotokanKarate', tempo: 30,
      ownershipTier: 'Style', ownershipGroup: 'ShotokanKarate', family: 'KineticImpact',
      seasonal: SEASON, displayId: 'SFS-003',
      traits: ['Punch', 'Counter', 'Rooted'],
      moveGloss: 'Sweep the low line aside and drive a reverse punch upstairs.',
      attackBeat: 'sweeps the low line aside and drives a reverse punch up the middle',
      defenseBeat: 'hammers the downward block across the low line and holds the stance',
      defenseMethod: 'Deflect',
      attack: {
        optimalRanges: ['Pocket'], levels: ['High'], power: 3, cost: 0, category: 'Punch',
        isCounter: true,
        // Designer: modifyPower +1, condition `pairedDefenseFullGuardAtLevel { level: 'Low',
        // attackMustBeUnresolved: true }`.
        //
        // `isFullGuard`, not `fullyStopped` — §8 additionally requires final Power of at least 1, and
        // the two disagree on an Attack ground to 0 before the Guard check. Same reading cross_counter
        // uses, and the reason is the same: you cannot claim to have stopped something that was
        // already nothing.
        powerMod: (ctx) => {
          if (!ctx.actingSecond) return 0;
          const incoming = ctx.results[ctx.opp];
          if (!incoming || incoming.level !== 'Low') return 0;
          return ctx.isFullGuard(incoming) ? 1 : 0;
        },
        text: 'Opt P • H • P3. If your committed Defense Fully Guards a Low Attack before this ' +
              'resolves, +1 Power.'
      },
      defense: { levels: ['Mid', 'Low'], guard: 4, text: 'M/L • G4.' },
      v4: {
        technique: 'Gedan-Barai Counter', family: 'Punch', speedPips: 2, speedLabel: 'Standard',
        continuation: { kind: 'Chain', openingLevel: 'Mid', openingType: 'Combination', bonus: 1,
                        usableBy: ['Punch'], available: 'nextExchangeOnly', trigger: 'contact' },
        defenseFamily: 'Deflect',
        defenseResult: { contact: false, cancelContinuation: true, openingOn: 'attacker',
                         openingLevel: 'attemptedLevelUnlessRecoveryPrinted', bonus: 1,
                         againstGrappleControl: 'holdClash' },
        attackBadge: 'PUNCH - CHAIN -> MID: Contact creates the printed Punch-only Opening for next exchange.',
        defenseBadge: 'DEFLECT - Cancel continuation; create an Opening on the attacker at the resolved exposure level.',
        atkSummary: 'Range P | Level H | Power P3 | Punch',
        defSummary: 'Level M/L | Guard 4'
      }
    },

    // ---------------------------------------------------------------------------------------------
    // SFS-004 — Step-Behind Hook Kick (Taekwondo)
    //
    // Designer: +1 Power when `evadeMovedClashFarther` — the mirror of SFS-002's clause on the other
    // Intent. The cross-step IS the evasion, which is why the same card both moves away and hits
    // harder for having done so.
    // ---------------------------------------------------------------------------------------------
    sfs_step_behind_hook_kick: {
      id: 'sfs_step_behind_hook_kick', name: 'Step-Behind Hook Kick', style: 'Taekwondo', tempo: 27,
      ownershipTier: 'Style', ownershipGroup: 'Taekwondo', family: 'KineticImpact',
      seasonal: SEASON, displayId: 'SFS-004',
      traits: ['Kick', 'Footwork'],
      moveGloss: 'Cross-step off line and whip the heel around the far side.',
      attackBeat: 'cross-steps off the line and whips the heel around the far side of the guard',
      defenseBeat: 'slides the back foot out and lets the heel pass through empty air',
      defenseMethod: 'AngleStep',
      attack: {
        optimalRanges: ['Long', 'Pocket'], levels: ['High'], power: 3, cost: 0, category: 'Kick',
        // Designer: modifyPower +1, condition `evadeMovedClashFarther`. The attacker's own Intent,
        // read off attChoice — the same shape SFS-002 uses for Pressure.
        powerMod: (ctx) => (ctx.attChoice.positionIntent === 'Evade' && ctx.clashFarther) ? 1 : 0,
        text: 'Opt L/P • H • P3. If you chose Evade and Clash is farther than Start, +1 Power.'
      },
      defense: { levels: ['High', 'Low'], guard: 2, text: 'H/L • G2. On a whiff, End 1 band farther.' },
      v4: {
        technique: 'Step-Behind Hook Kick', family: 'Kick', speedPips: 2, speedLabel: 'Standard',
        continuation: { kind: 'ImpactRecovery', impactOnAbsorb: 1, recoveryOpeningLevel: 'High',
                        recoveryOpeningType: 'Recovery', recoveryOpeningBonus: 1,
                        recoveryTrigger: ['Deflect', 'Dodge'] },
        defenseFamily: 'Dodge',
        defenseResult: { contact: false, attackBecomesWhiff: true,
                         end: { kind: 'shift', direction: 'farther', bands: 1 } },
        attackBadge: 'KICK - IMPACT/RECOVERY: Absorb costs 1 Stamina; Deflect or Dodge creates a High Recovery Opening.',
        defenseBadge: 'DODGE - Attack Whiffs; move End Range 1 band farther.',
        atkSummary: 'Range L/P | Level H | Power P3 | Kick',
        defSummary: 'Level H/L | Guard 2'
      }
    },

    // ---------------------------------------------------------------------------------------------
    // SFS-005 — Jut-Sao Straight (Wing Chun)
    //
    // Designer: `repeatStrike` at Mid Power 1, after the primary strike, if this Attack SPENT a
    // Combination Opening. One of the two cards the Phase C engine surface exists for — see
    // ctx.openingSpent() in js/engine.js, and tests/engine-season-surface-test.js for why the answer
    // cannot be read off the defender by the time a card can ask.
    // ---------------------------------------------------------------------------------------------
    sfs_jut_sao_straight: {
      id: 'sfs_jut_sao_straight', name: 'Jut-Sao Straight', style: 'WingChun', tempo: 16,
      ownershipTier: 'Style', ownershipGroup: 'WingChun', family: 'FulcrumFlow',
      seasonal: SEASON, displayId: 'SFS-005',
      traits: ['Punch', 'Trap', 'Combination'],
      moveGloss: 'Jerk the guard down and fire a straight punch up the center.',
      attackBeat: 'jerks the guard down with a short pull and fires a straight up the centerline',
      defenseBeat: 'jams the centerline and takes the lane away before it opens',
      defenseMethod: 'Trap',
      attack: {
        optimalRanges: ['Pocket', 'Clinch'], levels: ['High'], power: 3, cost: 0, category: 'Punch',
        // `afterAlways`, so the repeat is decided AFTER the primary strike has resolved and can ask
        // whether that strike spent an Opening. Part of this card's own resolution — same Speed
        // group, no second continuation — which is what `extraStrike` is.
        afterAlways: (ctx) => {
          const spent = ctx.openingSpent();
          if (spent && spent.type === 'Combination') ctx.extraStrike('Mid', 1);
        },
        text: 'Opt P/C • H • P3. If this Attack spent a Combination Opening, repeat at Mid for P1.'
      },
      defense: { levels: ['High', 'Mid'], guard: 3, text: 'H/M • G3.' },
      v4: {
        technique: 'Jut-Sao Straight', family: 'Punch', speedPips: 3, speedLabel: 'Fast',
        continuation: { kind: 'Chain', openingLevel: 'Low', openingType: 'Combination', bonus: 1,
                        usableBy: ['Punch'], available: 'nextExchangeOnly', trigger: 'contact' },
        defenseFamily: 'Deny',
        defenseResult: { contact: false, cancelContinuation: true, control: 'setPocket' },
        attackBadge: 'PUNCH - CHAIN -> LOW: Contact creates the printed Punch-only Opening for next exchange.',
        defenseBadge: 'DENY - Cancel continuation; set End at Pocket.',
        atkSummary: 'Range P/C | Level H | Power P3 | Punch',
        defSummary: 'Level H/M | Guard 3'
      }
    },

    // ---------------------------------------------------------------------------------------------
    // SFS-006 — Reverse Elbow (Pencak Silat)
    //
    // Designer: ignore 1 Guard when `clashDiffersFromStart`. `ctx.clashChanged`, which is the shipped
    // reading — limb_destruction and outside_blade already use it, at 2 Guard rather than 1.
    // ---------------------------------------------------------------------------------------------
    sfs_reverse_elbow: {
      id: 'sfs_reverse_elbow', name: 'Reverse Elbow', style: 'PencakSilat', tempo: 29,
      ownershipTier: 'Style', ownershipGroup: 'PencakSilat', family: 'FulcrumFlow',
      seasonal: SEASON, displayId: 'SFS-006',
      traits: ['Elbow', 'Angle'],
      moveGloss: 'Turn past the lead shoulder and whip the rear elbow backward.',
      attackBeat: 'turns past the lead shoulder and whips the rear elbow back through the jaw line',
      defenseBeat: 'rolls off the angle and lets the elbow carry past',
      defenseMethod: 'AngleStep',
      attack: {
        optimalRanges: ['Pocket', 'Clinch'], levels: ['High'], power: 3, cost: 0, category: 'Elbow',
        guardIgnore: (ctx) => ctx.clashChanged ? 1 : 0,
        text: 'Opt P/C • H • P3. If Clash differs from Start, ignore 1 Guard.'
      },
      defense: { levels: ['High', 'Low'], guard: 3, text: 'H/L • G3. On a whiff, End 1 band farther.' },
      v4: {
        technique: 'Reverse Elbow', family: null, speedPips: 2, speedLabel: 'Standard',
        continuation: { kind: 'ImpactRecovery', impactOnAbsorb: 1, recoveryOpeningLevel: 'High',
                        recoveryOpeningType: 'Recovery', recoveryOpeningBonus: 1,
                        recoveryTrigger: ['Deflect', 'Dodge'] },
        defenseFamily: 'Dodge',
        defenseResult: { contact: false, attackBecomesWhiff: true,
                         end: { kind: 'shift', direction: 'farther', bands: 1 } },
        attackBadge: 'REVERSE ELBOW - IMPACT/RECOVERY: Absorb costs 1 Stamina; Deflect or Dodge creates a High Recovery Opening.',
        defenseBadge: 'DODGE - Attack Whiffs; move End Range 1 band farther.',
        atkSummary: 'Range P/C | Level H | Power P3 | Reverse Elbow',
        defSummary: 'Level H/L | Guard 3'
      }
    },

    // ---------------------------------------------------------------------------------------------
    // SFS-007 — Slide-By (Wrestling)
    //
    // Designer: a Grapple that attacks HIGH — the tag is `wrestling-high-line`, and it is the point
    // of the card. Wrestling's shipped entries are almost all Low; this one buys the Clinch from
    // the top instead.
    // ---------------------------------------------------------------------------------------------
    sfs_slide_by: {
      id: 'sfs_slide_by', name: 'Slide-By', style: 'Wrestling', tempo: 17,
      ownershipTier: 'Style', ownershipGroup: 'Wrestling', family: 'LeverageBase',
      seasonal: SEASON, displayId: 'SFS-007',
      traits: ['Grapple', 'Grip', 'Entry'],
      moveGloss: "Shuck the arm across and slip behind the opponent's shoulder.",
      attackBeat: 'shucks the arm across and slips in behind the shoulder',
      defenseBeat: 'fights the grip off and squares back up before the angle opens',
      defenseMethod: 'GripFight',
      attack: {
        optimalRanges: ['Pocket', 'Clinch'], levels: ['High'], power: 2, cost: 0, category: 'Grapple',
        text: 'Opt P/C • H • P2. Contact sets End at Clinch.'
      },
      defense: { levels: ['High', 'Mid'], guard: 3, text: 'H/M • G3.' },
      v4: {
        technique: 'Slide-By', family: 'Grapple', speedPips: 3, speedLabel: 'Fast',
        continuation: { kind: 'Control', contactResult: 'setClinch', trigger: 'contact' },
        defenseFamily: 'Deflect',
        defenseResult: { contact: false, cancelContinuation: true, openingOn: 'attacker',
                         openingLevel: 'attemptedLevelUnlessRecoveryPrinted', bonus: 1,
                         againstGrappleControl: 'holdClash' },
        attackBadge: 'GRAPPLE - CONTROL: Damaging Contact or Absorb sets End Range to Clinch.',
        defenseBadge: 'DEFLECT - Cancel continuation; create an Opening on the attacker at the resolved exposure level.',
        atkSummary: 'Range P/C | Level H | Power P2 | Grapple',
        defSummary: 'Level H/M | Guard 3'
      }
    },

    // ---------------------------------------------------------------------------------------------
    // SFS-008 — Uki Otoshi (Judo)
    //
    // Designer: Speed 1, Power 4, costs 1 Stamina at commit, and REQUIRES the opponent to be
    // carrying an Opening. The season's only committed finisher, and the only one of the fifteen
    // that cannot simply be played.
    // ---------------------------------------------------------------------------------------------
    sfs_uki_otoshi: {
      id: 'sfs_uki_otoshi', name: 'Uki Otoshi', style: 'Judo', tempo: 38,
      ownershipTier: 'Style', ownershipGroup: 'Judo', family: 'LeverageBase',
      seasonal: SEASON, displayId: 'SFS-008',
      traits: ['Grapple', 'Drop', 'Kuzushi'],
      moveGloss: 'Draw the upper body forward and drop beneath the empty corner.',
      attackBeat: 'draws the upper body forward and drops clean beneath the empty corner',
      defenseBeat: 'kills the kuzushi early and refuses to be drawn past their feet',
      defenseMethod: 'Post',
      // Designer: `requirements: [{ type: 'opponentHasOpening', minimum: 1 }]`. The shipped
      // requirement idiom takes (fighter, opponent) and gates the card at selection — the same
      // shape hip_throw's `opp.offBalance` uses.
      requirement: (f, opp) => (opp.openings || []).length >= 1,
      requirementText: 'Requires an Opening on the opponent.',
      attack: {
        optimalRanges: ['Pocket', 'Clinch'], levels: ['High'], power: 4, cost: 1, category: 'Grapple',
        text: 'Opt P/C • H • P4 • Cost 1. Requires an Opening on the opponent. Contact sets End at Pocket.'
      },
      defense: { levels: ['High', 'Low'], guard: 3, text: 'H/L • G3.' },
      v4: {
        technique: 'Uki Otoshi', family: 'Grapple', speedPips: 1, speedLabel: 'Committed',
        continuation: { kind: 'Control', contactResult: 'setPocket', trigger: 'contact' },
        defenseFamily: 'Deny',
        defenseResult: { contact: false, cancelContinuation: true, control: 'setPocket' },
        attackBadge: 'GRAPPLE - CONTROL: Damaging Contact or Absorb sets End Range to Pocket.',
        defenseBadge: 'DENY - Cancel continuation; set End at Pocket.',
        atkSummary: 'Range P/C | Level H | Power P4 | Cost 1 | Grapple',
        defSummary: 'Level H/L | Guard 3'
      }
    },

    // ---------------------------------------------------------------------------------------------
    // SFS-009 — Rebound Front Kick (Kinetic Impact)
    //
    // Designer: +1 Power if your PREVIOUS Attack dealt no damage. The plan expected this to need new
    // engine memory; it does not — `_dealtDamageLastExchange` is already stamped on both fighters at
    // the end of every exchange, so the condition is a read.
    // ---------------------------------------------------------------------------------------------
    sfs_rebound_front_kick: {
      id: 'sfs_rebound_front_kick', name: 'Rebound Front Kick', style: 'KineticImpact', tempo: 15,
      ownershipTier: 'Family', ownershipGroup: 'KineticImpact',
      seasonal: SEASON, displayId: 'SFS-009',
      traits: ['Kick', 'Recovery', 'Fast'],
      moveGloss: 'Recoil from the miss and spring the lead foot back up the line.',
      attackBeat: 'recoils off the miss and springs the lead foot straight back up the line',
      defenseBeat: 'gives ground on the rebound and lets the foot fall short',
      defenseMethod: 'Backstep',
      attack: {
        optimalRanges: ['Long', 'Pocket'], levels: ['Mid'], power: 2, cost: 0, category: 'Kick',
        // Designer: condition `previousOwnAttackDamage equals 0`. The flag is false on the first
        // exchange of a bout too, which is the right reading: nothing landed, because nothing was
        // thrown.
        powerMod: (ctx) => ctx.attacker._dealtDamageLastExchange ? 0 : 1,
        // A CHAMBER LOADER (RULES.md §19). The only seasonal card in the package, and the reason it
        // is in it at all is that its printed flavour was already the mechanic: a kick that gets
        // stopped recoils into another chamber. It is deliberately NOT in any shipped deck or in
        // either documented upgrade module -- a store card cannot be a no-credit upgrade path -- so
        // this clause only ever fires for a player who owns the season and chose to build with it.
        // The recovery-line reward above is untouched; the two conditions are independent.
        chamber: true,
        onResolved: (ctx, result) => { if (result.fullyStopped) ctx.gainChamber(ctx.self, 1); },
        text: 'Opt L/P • M • P2. If your last Attack dealt no damage, +1 Power. If fully stopped, gain 1 Chamber.'
      },
      defense: { levels: ['Mid', 'Low'], guard: 3, text: 'M/L • G3. On a whiff, End 1 band farther.' },
      v4: {
        technique: 'Rebound Front Kick', family: 'Kick', speedPips: 3, speedLabel: 'Fast',
        continuation: { kind: 'ImpactRecovery', impactOnAbsorb: 1, recoveryOpeningLevel: 'Mid',
                        recoveryOpeningType: 'Recovery', recoveryOpeningBonus: 1,
                        recoveryTrigger: ['Deflect', 'Dodge'] },
        defenseFamily: 'Dodge',
        defenseResult: { contact: false, attackBecomesWhiff: true,
                         end: { kind: 'shift', direction: 'farther', bands: 1 } },
        attackBadge: 'KICK - IMPACT/RECOVERY: Absorb costs 1 Stamina; Deflect or Dodge creates a Mid Recovery Opening.',
        defenseBadge: 'DODGE - Attack Whiffs; move End Range 1 band farther.',
        atkSummary: 'Range L/P | Level M | Power P2 | Kick',
        defSummary: 'Level M/L | Guard 3'
      }
    },

    // ---------------------------------------------------------------------------------------------
    // SFS-010 — Compact Frame (Torque & Pressure)
    // ---------------------------------------------------------------------------------------------
    sfs_compact_frame: {
      id: 'sfs_compact_frame', name: 'Compact Frame', style: 'TorquePressure', tempo: 25,
      ownershipTier: 'Family', ownershipGroup: 'TorquePressure',
      seasonal: SEASON, displayId: 'SFS-010',
      traits: ['Punch', 'Pressure', 'Frame'],
      moveGloss: 'Crowd the shoulders behind a tight frame and turn into the body.',
      attackBeat: 'crowds in behind a tight frame and turns a short hand into the body',
      defenseBeat: 'takes it on the frame and gives up nothing but ground',
      defenseMethod: 'Frame',
      attack: {
        optimalRanges: ['Pocket', 'Clinch'], levels: ['Mid'], power: 3, cost: 0, category: 'Punch',
        powerMod: (ctx) => (ctx.attChoice.positionIntent === 'Pressure' && ctx.clashCloser) ? 1 : 0,
        text: 'Opt P/C • M • P3. If you chose Pressure and Clash is closer than Start, +1 Power.'
      },
      defense: { levels: ['High', 'Low'], guard: 3, text: 'H/L • G3.' },
      v4: {
        technique: 'Compact Frame', family: 'Punch', speedPips: 2, speedLabel: 'Standard',
        continuation: { kind: 'Chain', openingLevel: 'High', openingType: 'Combination', bonus: 1,
                        usableBy: ['Punch'], available: 'nextExchangeOnly', trigger: 'contact' },
        defenseFamily: 'Absorb',
        defenseResult: { contact: true, recover: 1, condition: 'noStaminaLostFromIncomingAttack' },
        attackBadge: 'PUNCH - CHAIN -> HIGH: Contact creates the printed Punch-only Opening for next exchange.',
        defenseBadge: 'ABSORB - Contact; Recover 1 if the incoming Attack caused no Stamina loss.',
        atkSummary: 'Range P/C | Level M | Power P3 | Punch',
        defSummary: 'Level H/L | Guard 3'
      }
    },

    // ---------------------------------------------------------------------------------------------
    // SFS-011 — Split-Gate Entry (Fulcrum & Flow)
    //
    // The second card the Phase C engine surface exists for: ignore 1 Guard if this Attack SPENT a
    // Combination Opening. Its Defense also shifts the holder toward Flow on a full guard — the
    // designer's `shiftFlow -1`, read against a track where Fury is positive and Flow negative,
    // which is this engine's `furyFlow` exactly. `ctx.shiftFlow(key, 1)` moves one step toward Flow.
    // ---------------------------------------------------------------------------------------------
    sfs_split_gate_entry: {
      id: 'sfs_split_gate_entry', name: 'Split-Gate Entry', style: 'FulcrumFlow', tempo: 14,
      ownershipTier: 'Family', ownershipGroup: 'FulcrumFlow',
      seasonal: SEASON, displayId: 'SFS-011',
      traits: ['Punch', 'Entry', 'Combination'],
      moveGloss: 'Wedge between the forearms and send a straight hand through center.',
      attackBeat: 'wedges between the forearms and sends a straight hand through the center',
      defenseBeat: 'splits the entry off the centerline and settles',
      defenseMethod: 'Deflect',
      attack: {
        optimalRanges: ['Pocket', 'Clinch'], levels: ['Mid'], power: 2, cost: 0, category: 'Punch',
        guardIgnore: (ctx) => {
          const spent = ctx.openingSpent();
          return (spent && spent.type === 'Combination') ? 1 : 0;
        },
        text: 'Opt P/C • M • P2. If this Attack spent a Combination Opening, ignore 1 Guard.'
      },
      defense: {
        levels: ['High', 'Low'], guard: 3,
        // Designer: shiftFlow, aftermath, self, on `fullGuard`. `ctx.opp` is this card's own fighter
        // in a Defense hook — the trap probe_jab's comment records having been caught by.
        onResolved: (ctx, result) => { if (result.fullyStopped) ctx.shiftFlow(ctx.opp, 1); },
        text: 'H/L • G3. On a full guard, shift Flow -1.'
      },
      v4: {
        technique: 'Split-Gate Entry', family: 'Punch', speedPips: 3, speedLabel: 'Fast',
        continuation: { kind: 'Chain', openingLevel: 'High', openingType: 'Combination', bonus: 1,
                        usableBy: ['Punch'], available: 'nextExchangeOnly', trigger: 'contact' },
        defenseFamily: 'Deflect',
        defenseResult: { contact: false, cancelContinuation: true, openingOn: 'attacker',
                         openingLevel: 'attemptedLevelUnlessRecoveryPrinted', bonus: 1,
                         againstGrappleControl: 'holdClash' },
        attackBadge: 'PUNCH - CHAIN -> HIGH: Contact creates the printed Punch-only Opening for next exchange.',
        defenseBadge: 'DEFLECT - Cancel continuation; create an Opening on the attacker at the resolved exposure level.',
        atkSummary: 'Range P/C | Level M | Power P2 | Punch',
        defSummary: 'Level H/L | Guard 3'
      }
    },

    // ---------------------------------------------------------------------------------------------
    // SFS-012 — Two-on-One Turn (Leverage & Base)
    //
    // Designer: ignore 1 Guard if the opposing Defense covers TWO OR MORE levels. The shipped idiom
    // reads `defCard.defense.levels` and handles the 'All' string as well as an array — see
    // arm_drag and lift_and_turn, which both do this at 2 Guard.
    // ---------------------------------------------------------------------------------------------
    sfs_two_on_one_turn: {
      id: 'sfs_two_on_one_turn', name: 'Two-on-One Turn', style: 'LeverageBase', tempo: 24,
      ownershipTier: 'Family', ownershipGroup: 'LeverageBase',
      seasonal: SEASON, displayId: 'SFS-012',
      traits: ['Grapple', 'Grip', 'Control'],
      moveGloss: 'Secure wrist and elbow, then wheel the shoulders off the line.',
      attackBeat: 'secures wrist and elbow and wheels the shoulders right off the line',
      defenseBeat: 'strips the two-on-one and re-centers before the turn arrives',
      defenseMethod: 'GripFight',
      attack: {
        optimalRanges: ['Pocket', 'Clinch'], levels: ['High'], power: 2, cost: 0, category: 'Grapple',
        guardIgnore: (ctx) => {
          const lv = ctx.defCard.defense.levels;
          return (lv === 'All' || (Array.isArray(lv) && lv.length >= 2)) ? 1 : 0;
        },
        text: 'Opt P/C • H • P2. If their Defense covers 2 or more levels, ignore 1 Guard.'
      },
      defense: { levels: ['High', 'Mid'], guard: 3, text: 'H/M • G3.' },
      v4: {
        technique: 'Two-on-One Turn', family: 'Grapple', speedPips: 2, speedLabel: 'Standard',
        continuation: { kind: 'Control', contactResult: 'setPocket', trigger: 'contact' },
        defenseFamily: 'Deny',
        defenseResult: { contact: false, cancelContinuation: true, control: 'setPocket' },
        attackBadge: 'GRAPPLE - CONTROL: Damaging Contact or Absorb sets End Range to Pocket.',
        defenseBadge: 'DENY - Cancel continuation; set End at Pocket.',
        atkSummary: 'Range P/C | Level H | Power P2 | Grapple',
        defSummary: 'Level H/M | Guard 3'
      }
    },

    // ---------------------------------------------------------------------------------------------
    // SFS-013 — Sprawl and Rise (Universal)
    //
    // The season's low-line safety valve: a Deny that covers LOW ONLY, at Guard 4. Nothing else in
    // the Universal pool answers the low line that hard, which is the designer's stated reason for
    // it being Universal rather than Wrestling's.
    // ---------------------------------------------------------------------------------------------
    sfs_sprawl_and_rise: {
      id: 'sfs_sprawl_and_rise', name: 'Sprawl and Rise', style: 'Universal', tempo: 26,
      ownershipTier: 'Universal', ownershipGroup: 'Fundamentals',
      seasonal: SEASON, displayId: 'SFS-013',
      traits: ['Punch', 'Sprawl', 'Fundamental'],
      moveGloss: 'Drop the hips into the entry, then rise behind a short uppercut.',
      attackBeat: 'drops the hips into the entry and rises behind a short uppercut',
      defenseBeat: 'sprawls the hips back and kills the level change at the floor',
      defenseMethod: 'Sprawl',
      attack: {
        optimalRanges: ['Pocket', 'Clinch'], levels: ['High'], power: 2, cost: 0, category: 'Punch',
        text: 'Opt P/C • H • P2. Contact chains to a Mid Opening.'
      },
      defense: { levels: ['Low'], guard: 4, text: 'L • G4.' },
      v4: {
        technique: 'Sprawl and Rise', family: 'Punch', speedPips: 2, speedLabel: 'Standard',
        continuation: { kind: 'Chain', openingLevel: 'Mid', openingType: 'Combination', bonus: 1,
                        usableBy: ['Punch'], available: 'nextExchangeOnly', trigger: 'contact' },
        defenseFamily: 'Deny',
        defenseResult: { contact: false, cancelContinuation: true, control: 'setPocket' },
        attackBadge: 'PUNCH - CHAIN -> MID: Contact creates the printed Punch-only Opening for next exchange.',
        defenseBadge: 'DENY - Cancel continuation; set End at Pocket.',
        atkSummary: 'Range P/C | Level H | Power P2 | Punch',
        defSummary: 'Level L | Guard 4'
      }
    },

    // ---------------------------------------------------------------------------------------------
    // SFS-014 — Split the Guard (Universal)
    // ---------------------------------------------------------------------------------------------
    sfs_split_the_guard: {
      id: 'sfs_split_the_guard', name: 'Split the Guard', style: 'Universal', tempo: 15,
      ownershipTier: 'Universal', ownershipGroup: 'Fundamentals',
      seasonal: SEASON, displayId: 'SFS-014',
      traits: ['Punch', 'Fundamental', 'Combination'],
      moveGloss: 'Thread a narrow straight through the seam between both forearms.',
      attackBeat: 'threads a narrow straight through the seam between both forearms',
      defenseBeat: 'turns the seam shut and deflects the straight wide',
      defenseMethod: 'Deflect',
      attack: {
        optimalRanges: ['Pocket', 'Clinch'], levels: ['Mid'], power: 2, cost: 0, category: 'Punch',
        guardIgnore: (ctx) => {
          const lv = ctx.defCard.defense.levels;
          return (lv === 'All' || (Array.isArray(lv) && lv.length >= 2)) ? 1 : 0;
        },
        text: 'Opt P/C • M • P2. If their Defense covers 2 or more levels, ignore 1 Guard.'
      },
      defense: { levels: ['High', 'Low'], guard: 2, text: 'H/L • G2.' },
      v4: {
        technique: 'Split the Guard', family: 'Punch', speedPips: 3, speedLabel: 'Fast',
        continuation: { kind: 'Chain', openingLevel: 'High', openingType: 'Combination', bonus: 1,
                        usableBy: ['Punch'], available: 'nextExchangeOnly', trigger: 'contact' },
        defenseFamily: 'Deflect',
        defenseResult: { contact: false, cancelContinuation: true, openingOn: 'attacker',
                         openingLevel: 'attemptedLevelUnlessRecoveryPrinted', bonus: 1,
                         againstGrappleControl: 'holdClash' },
        attackBadge: 'PUNCH - CHAIN -> HIGH: Contact creates the printed Punch-only Opening for next exchange.',
        defenseBadge: 'DEFLECT - Cancel continuation; create an Opening on the attacker at the resolved exposure level.',
        atkSummary: 'Range P/C | Level M | Power P2 | Punch',
        defSummary: 'Level H/L | Guard 2'
      }
    },

    // ---------------------------------------------------------------------------------------------
    // SFS-015 — Smothering Clinch (Universal)
    //
    // The one card in the season that takes something away. Designer: `suppressRecovery`, sources
    // ['Flow'], duration currentExchange, condition `attackDealsDamage`.
    //
    // THE DAMAGE CONDITION IS GONE, AND ITS REMOVAL IS THE POINT. On 2026-08-20 Flow recovery
    // itself became conditional on not being damaged (RULES.md 12), which left `attackDealsDamage`
    // asking exactly the question the base rule now asks. The two are the same question, so the
    // rider could never fire: measured at ZERO firings across 1,200 bouts and 290 plays of the
    // card, under two agents and two seeds. A card sold for credits whose printed clause is
    // unreachable is worse than one that prints no clause at all.
    //
    // So the LETTER of the designer's spec is dropped and its INTENT kept. The package's own
    // balanceTags say `flow-recovery-counterplay`, and counter-play is precisely what had stopped
    // existing. The other two bounds are untouched and still enforced — see
    // ctx.suppressFlowRecovery in js/engine.js, where the exclusions (never Absorb, never
    // card-authored recovery, never a second exchange) are argued at the site that honours them.
    //
    // WHY UNCONDITIONAL RATHER THAN A NARROWER GATE. Both alternatives were measured against a
    // Wing Chun deck built to camp at Flow, 300 bouts per arm:
    //   - Gated on Optimal -- you have to be in the clinch to smother one -- fired 1 and 5 times
    //     under the track-seeking agent against 25-30 under random. A Fulcrum & Flow camper
    //     controls range, so that gate fails hardest against the one deck this card answers.
    //   - Carrying the smother into the next exchange needs a new rollover field in the engine and
    //     fired 2 to 9 times. Engine surface bought nothing.
    // Unconditional fires on about a third of the card's plays (32-53 per 300 bouts) with no
    // detectable change in win rate at that sample size. That is the profile a tech card should
    // have: dead against most decks, live against the one it is printed for.
    //
    // A WHIFF CANNOT REACH THIS HOOK, so "unconditional" is narrower than it reads. js/engine.js
    // calls an Attack's onResolved only in the non-Whiff branches; the Whiff path runs onWhiff
    // instead, on both halves since 2026-08-21 (it was defence-only before, which is a fix this
    // card does not care about either way). And this card could not Whiff regardless: its optimal
    // set is Pocket/Clinch and Long is band-distance 1 from Pocket, so it is Compromised at worst.
    // That second fact is why no `!result.whiffed` guard is written below. It would be a clause
    // that can never be false, which is the exact defect this change exists to remove.
    // ---------------------------------------------------------------------------------------------
    sfs_smothering_clinch: {
      id: 'sfs_smothering_clinch', name: 'Smothering Clinch', style: 'Universal', tempo: 27,
      ownershipTier: 'Universal', ownershipGroup: 'Fundamentals',
      seasonal: SEASON, displayId: 'SFS-015',
      traits: ['Grapple', 'Clinch', 'Attrition'],
      moveGloss: 'Clamp the arms and lean chest-to-chest to drain their posture.',
      attackBeat: 'clamps both arms and leans chest-to-chest until the posture folds',
      defenseBeat: 'keeps the chest off theirs and denies the clamp anywhere to settle',
      defenseMethod: 'Underhook',
      attack: {
        optimalRanges: ['Pocket', 'Clinch'], levels: ['Mid'], power: 2, cost: 0, category: 'Grapple',
        onResolved: (ctx) => { ctx.suppressFlowRecovery(ctx.opp); },
        text: 'Opt P/C • M • P2. The opponent takes no Flow recovery this exchange. Contact ' +
              'holds the Clash range.'
      },
      defense: { levels: ['High', 'Mid'], guard: 3, text: 'H/M • G3.' },
      v4: {
        technique: 'Smothering Clinch', family: 'Grapple', speedPips: 2, speedLabel: 'Standard',
        continuation: { kind: 'Control', contactResult: 'holdClash', trigger: 'contact' },
        defenseFamily: 'Deny',
        defenseResult: { contact: false, cancelContinuation: true, control: 'setPocket' },
        attackBadge: 'GRAPPLE - CONTROL: Damaging Contact or Absorb holds the Clash range.',
        defenseBadge: 'DENY - Cancel continuation; set End at Pocket.',
        atkSummary: 'Range P/C | Level M | Power P2 | Grapple',
        defSummary: 'Level H/M | Guard 3'
      }
    }
  });

  // The season's own id list, exported so tests and the eventual store can ask "what did this
  // season add" without string-matching an id prefix. Derived from the cards rather than written
  // twice.
  window.SEASON_CARDS = window.SEASON_CARDS || {};
  window.SEASON_CARDS[SEASON] = Object.keys(window.CARDS).filter(id => window.CARDS[id].seasonal === SEASON);
})();
